import { cn } from '@invent/wl-ui-kit-next';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@invent/wl-ui-kit-next/ui';
import { ArrowDown, ArrowUp, Filter } from 'lucide-react';
import type * as React from 'react';

/**
 * The compliance queues' column headers (SR-485), in the look of Lists' (`lists/grid.tsx`'s
 * `Header`, `lists/funnel.tsx`): the label's press sorts, a funnel filters — each over the page's
 * own URL keys, since survey-api's work-item list takes no criteria.
 *
 * **A sort is one of survey-api's three orders**, each with its own fixed direction (`sort`:
 * `raised_at` and `updated_at` newest first, `due_on` earliest first, reviewed last): a press on
 * another column's label orders by it, a press on the order in force goes back to the default,
 * newest raised. There is no ascending or descending to toggle.
 */
export function SortHeader({
  label,
  direction,
  active,
  note,
  onPress,
  funnel
}: {
  label: string;
  /** The order's fixed direction — the arrow drawn while it is in force. */
  direction: 'asc' | 'desc';
  active: boolean;
  /** The order in words, in the tooltip. */
  note: string;
  onPress: () => void;
  funnel?: React.ReactNode;
}) {
  const Arrow = direction === 'desc' ? ArrowDown : ArrowUp;
  return (
    <span data-column={label} className="inline-flex items-center gap-0.5">
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={`${label}${active ? `, sorted ${direction === 'asc' ? 'ascending' : 'descending'}` : ''}`}
            onClick={onPress}
            className="inline-flex items-center gap-1 rounded px-0.5 outline-none hover:text-[var(--color-royal-blue)] focus-visible:ring-2 focus-visible:ring-ring"
          >
            {label}
            <Arrow className={cn('size-3', active ? 'opacity-100' : 'opacity-0')} aria-hidden />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">{note}</TooltipContent>
      </Tooltip>
      {funnel}
    </span>
  );
}

/** A plain label with a funnel after it. */
export function FunnelHeader({ label, funnel }: { label: string; funnel: React.ReactNode }) {
  return (
    <span data-column={label} className="inline-flex items-center gap-0.5">
      <span className="px-0.5">{label}</span>
      {funnel}
    </span>
  );
}

/** A header's funnel: the filter icon, filled while `said` names what it is set to, and a small
 *  popover of the page's own control for that key. */
export function ParamFunnel({
  code,
  label,
  said,
  children,
  open,
  onOpenChange
}: {
  /** `data-funnel`, where a spec finds it. */
  code: string;
  label: string;
  /** What the filter is set to, or null while it narrows nothing. */
  said: Nullable<string>;
  children: React.ReactNode;
  /** Held by the page, where a filter chip's press opens it. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={said ? `Filter ${label}: ${said}` : `Filter ${label}`}
          data-funnel={code}
          data-active={said ? '' : undefined}
          className={cn(
            'inline-flex size-6 shrink-0 items-center justify-center rounded outline-none hover:bg-[var(--accent)] focus-visible:ring-2 focus-visible:ring-ring',
            said ? 'text-[var(--color-teal)]' : 'text-[var(--veritant-royal-55)]'
          )}
        >
          <Filter className={cn('size-3.5', said && 'fill-current')} aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        aria-label={`Filter ${label}`}
        className="grid w-72 gap-2 p-3 text-[0.8125rem] font-normal"
      >
        {children}
      </PopoverContent>
    </Popover>
  );
}

/** One choice of several, as radio buttons — a single-valued funnel's body. */
export function FunnelChoice({
  label,
  value,
  options,
  onChoose
}: {
  label: string;
  value: Nullable<string>;
  options: readonly { value: Nullable<string>; label: string }[];
  onChoose: (value: Nullable<string>) => void;
}) {
  return (
    <fieldset aria-label={label} className="grid max-h-72 gap-1.5 overflow-y-auto">
      {options.map((option) => (
        // "Any" is null and a blank value is '' (Rankings' "No cycle"): keyed apart.
        <label key={option.value === null ? 'any' : `value:${option.value}`} className="flex items-center gap-2">
          <input
            type="radio"
            name={label}
            checked={option.value === value}
            onChange={() => onChoose(option.value)}
          />
          <span className="wrap-anywhere">{option.label}</span>
        </label>
      ))}
    </fieldset>
  );
}
